/*
================================================================================
File: src/pages/SellerLeads/SellerPages.js
Description: Displays seller leads with search, pagination, full action modals,
             Multi-Select Bulk Delete, Quick Lead creation, Map Mode, and
             AI Assistant Chat integration.
================================================================================
*/
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAdaptiveMMKV } from "../../hooks/useAdaptiveMMKV";
import moment from "moment";


import instance from "../../api/axios";
import { storage } from "../../App";
import { views } from "../../constants/views";
import PageContent from "../../components/common/PageContent";
import Modal from "../../components/common/Modal";
import AutocompleteInput from "../../components/common/AutocompleteInput";
import AIAssistantChat from "../../components/chat/AIAssistantChat";
import { FormGroup, SelectGroup } from "../../components/common/FormGroup";
import SellerPagesMapWrapper from "./SellerLeadsMap";
import { formatMobileNumber } from "../../utils/formatters";
import { tagGroups, stateOptions } from "../../constants/options";



const SellerPages = ({ onViewChange, user, pulldata }) => {

  const [dummyLeads, setDummyLeads] = useAdaptiveMMKV(
    "user.Feed.Seller.Leads",
    storage,
    [],
  );

  const leadsTruthRef = useRef(dummyLeads);
  useEffect(() => {
    leadsTruthRef.current = dummyLeads;
  }, [dummyLeads]);

  const [sellerAR] = useAdaptiveMMKV("user.Feed.Seller.Responders", storage);
  const [menuOpenForUser, setMenuOpenForUser] = useState(null);
  const [autoresponderSubMenuOpen, setAutoresponderSubMenuOpen] =
    useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const itemsPerPage = 6;
  const menuRef = useRef([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState("");
  const [pid, setPID] = useState(0);

  const [selectedLeads, setSelectedLeads] = useState([]);

  const [quickLeadModalOpen, setQuickLeadModalOpen] = useState(false);
  const [newLead, setNewLead] = useState({
    property_address: "",
    property_city: "",
    property_state: "",
    property_zip: "",
  });

  const [showMap, setShowMap] = useState(false);


  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = menuRef.current.every(
        (ref) => ref && !ref.contains(event.target),
      );
      if (isOutside) {
        setMenuOpenForUser(null);
        setAutoresponderSubMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // LINE 61: Enforce String matching to prevent wiping coordinates
const pullleadsdata = useCallback(async () => {
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=leads`,
      );
      if (Array.isArray(response.data)) {
        // BYPASS the functional setter bug using the absolute truth ref
        const currentTruth = leadsTruthRef.current || [];
        const newLeads = response.data;
        const oldLeadsMap = new Map(currentTruth.map(l => [String(l.pid), l]));

        const mergedLeads = newLeads.map(newLead => {
          const oldLead = oldLeadsMap.get(String(newLead.pid));
          if (oldLead && oldLead.latitude && oldLead.longitude) {
            return { ...newLead, latitude: oldLead.latitude, longitude: oldLead.longitude };
          }
          return newLead;
        });

        // Pass the raw array, NOT a function
        setDummyLeads(mergedLeads);
      }
      setError(null);
    } catch (e) {
      // ... error handling
    }
  }, [user.app_id, setDummyLeads]);

  // LINE 89: Kill the infinite loop
  useEffect(() => {
    pulldata();
    pullleadsdata();
    pullSellerResponders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // CHANGE 3: Leave this array empty.

  const pullSellerResponders = useCallback(async () => {
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=autoresponderSellerDISTINCT`,
      );
      storage.set("user.Feed.Seller.Responders", JSON.stringify(response.data));
    } catch (e) {
      console.error("Error fetching autoresponders:", e);
    }
  }, [user.app_id]);

  const handleSelectRow = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleQuickLeadChange = (e) => {
    const { name, value } = e.target;
    setNewLead((prev) => ({ ...prev, [name]: value }));
  };

  const handlePlaceSelected = useCallback((place) => {
    const addressComponents = place.address_components || [];
    let city = "";
    let state = "";
    let zip = "";
    let streetNumber = "";
    let route = "";

    addressComponents.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) {
        city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
      if (types.includes("postal_code")) {
        zip = component.long_name;
      }
      if (types.includes("street_number")) {
        streetNumber = component.long_name;
      }
      if (types.includes("route")) {
        route = component.long_name;
      }
    });

    setNewLead({
      property_address: `${streetNumber} ${route}`.trim(),
      property_city: city,
      property_state: state,
      property_zip: zip,
    });
  }, []);

  const handleQuickLeadSubmit = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user?.app_id);
    formData.append("property_address", newLead.property_address);
    formData.append("property_city", newLead.property_city);
    formData.append("property_state", newLead.property_state);
    formData.append("property_zip", newLead.property_zip);
    formData.append("status", "New Lead");

    try {
      await instance.post("/createLead.php", formData);
      setQuickLeadModalOpen(false);
      setNewLead({
        property_address: "",
        property_city: "",
        property_state: "",
        property_zip: "",
      });
      setError(null);
    } catch (e) {
      const errorMessage =
        e.response?.data?.message || e.message || "An unknown error occurred.";
      setError(`Error creating lead: ${errorMessage}`);
    } finally {
      setLoading(false);
      await pulldata(user?.app_id);
      await pullleadsdata();
    }
  };

  const handleAction = useCallback(async (action, lead) => {
    setLoading(true);
    try {
      let url = "";
      const formData = new FormData();

      // 1. Construct Payload
      if (action === "bulk_delete") {
        formData.append("ids", selectedLeads.join(","));
        formData.append("owner", user?.app_id);
        formData.append("type", "seller_leads");
        url = "/bulkDelete.php";
      } else {
        formData.append("pid", lead?.pid);
        formData.append("owner", user?.app_id);

        switch (action) {
          case "notes":
            formData.append("notes", notes);
            url = "/createLead.php";
            break;
          case "podio":
            url = "/exportPodio.php";
            break;
          case "delete":
            url = "/deleteHome.php";
            break;
          case "skip_trace":
            formData.append("option", "primary");
            url = "/leadSkipTrace.php";
            break;
          case "skip_trace2":
            formData.append("option", "secondary");
            url = "/leadSkipTrace.php";
            break;
          case "auto_query":
            formData.append("prop_address", lead.property_address);
            formData.append("prop_city", lead.property_city);
            formData.append("prop_state", lead.property_state);
            url = "/aiQuery.php";
            break;
          default:
            setLoading(false);
            return;
        }
      }

      // 2. Execute Network Request
      await instance.post(url, formData);

      // 3. Optimistic UI Update (Bypassing functional setter bug via Ref)
      const currentTruth = leadsTruthRef.current || [];
      if (action === "delete") {
        setDummyLeads(currentTruth.filter((l) => l.pid !== lead.pid));
      } else if (action === "bulk_delete") {
        setDummyLeads(currentTruth.filter((l) => !selectedLeads.includes(l.pid)));
        setSelectedLeads([]);
      }

      // 4. Background Server Sync
      await pulldata(user?.app_id);
      await pullleadsdata();
      setError(null);
    } catch (e) {
      const errorMessage =
        e.response?.data?.message || e.message || "An unknown error occurred.";
      setError(`Error performing action "${action}": ${errorMessage}`);
    } finally {
      setLoading(false);
      setIsModalOpen(false);
      setMenuOpenForUser(null);
    }
  }, [notes, selectedLeads, user?.app_id, pulldata, pullleadsdata, setDummyLeads]);

  const handleSetAutoresponder = async (leadId, sequenceId) => {
    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("sequence", sequenceId);
    formData.append("pid", leadId);
    try {
      await instance.post("/setAutoResponder.php", formData);
      await pulldata(user?.app_id);
      await pullleadsdata();
      setError(null);
    } catch (e) {
      const errorMessage =
        e.response?.data?.message || e.message || "An unknown error occurred.";
      setError(`Error setting autoresponder: ${errorMessage}`);
    } finally {
      setAutoresponderSubMenuOpen(null);
      setMenuOpenForUser(null);
    }
  };

  const openModal = (action, lead) => {
    if (action === "bulk_delete") {
      setModalContent({ action, lead: { count: selectedLeads.length } });
    } else {
      setModalContent({ action, lead });
      if (action === "notes") {
        setNotes(lead.seller_motivation || "");
      }
    }
    setIsModalOpen(true);
  };

  const handleRowClick = useCallback((lead) => {
    onViewChange(views.editLead, "sellerleads", { lead });
  }, [onViewChange]);

  const filteredLeads = useMemo(
    () =>
      dummyLeads?.filter((lead) =>
        Object.values(lead).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      ) || [],
    [dummyLeads, searchTerm],
  );






  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentIds = currentLeads.map((l) => l.pid);
      setSelectedLeads((prev) => [...new Set([...prev, ...currentIds])]);
    } else {
      const currentIds = currentLeads.map((l) => l.pid);
      setSelectedLeads((prev) => prev.filter((id) => !currentIds.includes(id)));
    }
  };

  const getResponderShortName = (targetSequence) => {
    const foundResponder = sellerAR?.find(
      (responder) => String(responder.sequence) === String(targetSequence),
    );
    return foundResponder ? foundResponder.short_name : null;
  };

  const renderModalContent = () => {
    const { action, lead } = modalContent;
    if (!action || !lead) return null;

    switch (action) {
      case "notes":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Add or edit notes for <strong>{lead.property_address}</strong>
              </p>
            </div>
            <textarea
              className="w-full h-48 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 resize-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter your notes here..."
            />
          </div>
        );
      case "delete":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-red-800 dark:text-red-300">Delete Lead</p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  Are you sure you want to delete the lead for <strong>{lead.property_address}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        );
      case "bulk_delete":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="font-semibold text-red-800 dark:text-red-300">Delete {lead.count} Leads</p>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                  Are you sure you want to delete <strong>{lead.count}</strong> selected leads? This action cannot be undone.
                </p>
              </div>
            </div>
          </div>
        );
      case "podio":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.5 1.5H5a2 2 0 00-2 2v9a2 2 0 002 2h.006c.03.02.062.039.1.057l5.85-5.85a1.5 1.5 0 10-2.12-2.12L3.636 11.364a2 2 0 010-2.83l9.172-9.172a2 2 0 112.828 2.828l-7.07 7.07A3 3 0 1014 8a1 1 0 102 0 5 5 0 10-5.5 4.95z" />
              </svg>
              <p className="text-sm text-blue-800 dark:text-blue-300">
                This will send the lead for <strong>{lead.property_address}</strong> to your Podio account.
              </p>
            </div>
          </div>
        );
      case "analysis":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <p className="text-sm text-green-800 dark:text-green-300">
                This will generate a property analysis for <strong>{lead.property_address}</strong> and send it to your email.
              </p>
            </div>
          </div>
        );
      case "skip_trace":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-purple-800 dark:text-purple-300">
                This will use <strong>1 Skiptrace Credit</strong> to find information for <strong>{lead.property_address}</strong>.
              </p>
            </div>
          </div>
        );
      case "auto_query":
        return (
          <div className="space-y-4">
            <div className="flex gap-3 items-start p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
              <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
              </svg>
              <p className="text-sm text-indigo-800 dark:text-indigo-300">
                This will use <strong>1 AI Query Credit</strong> to populate data for <strong>{lead.property_address}</strong>.
              </p>
            </div>
          </div>
        );
      default:
        return <p>Are you sure you want to perform this action?</p>;
    }
  };

  const renderModalFooter = () => {
    const { action, lead } = modalContent;
    if (!action || !lead) return null;

    const buttonText = {
      notes: "Save Notes",
      delete: "Delete Lead",
      bulk_delete: "Delete All Selected",
      podio: "Send to Podio",
      analysis: "Generate Analysis",
      skip_trace: "Use Credit",
      auto_query: "Use Credit",
    };

    const isDestructive = action === "delete" || action === "bulk_delete";
    const buttonClass = isDestructive
      ? "px-5 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
      : "px-5 py-2 bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors";

    return (
      <button
        onClick={() => handleAction(action, lead)}
        disabled={loading}
        className={buttonClass}
      >
        {loading ? "Processing..." : buttonText[action] || "Confirm"}
      </button>
    );
  };

  const renderQuickLeadFooter = () => (
    <button
      onClick={handleQuickLeadSubmit}
      disabled={loading}
      className="px-5 py-2 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
    >
      {loading ? "Creating..." : "Create Lead"}
    </button>
  );

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-lg text-sm font-medium transition-colors duration-200
            ${
              currentPage === i
                ? "bg-orange-600 text-white shadow-md"
                : "bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500 border border-gray-300 dark:border-gray-600"
            }
          `}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-1 text-gray-400 dark:text-gray-500">
          ...
        </span>,
      );
    }

    return pageNumbers;
  };

  const closeModal = () => {
    setModalOpen(false);
    setPID(0);
  };

  const handleTagClick = async (tag) => {
    const formData = new FormData();
    formData.append("name", tag);
    formData.append("pid", pid);
    formData.append("owner", user.app_id);
    try {
      await instance({
        method: "POST",
        url: "/updateStatus.php",
        data: formData,
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (e) {
      console.error("Error updating status:", e);
    } finally {
      await pulldata(user?.app_id);
      closeModal();
    }
  };

  // Hero Banner - Orange/Amber theme for Seller Leads
  const HeroBanner = () => (
    <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-12 rounded-xl shadow-lg mb-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-white/20 rounded-lg">
              <svg
                className="w-8 h-8"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5m-15-4h9m-9 4h9m-9 4h6M10 1.5v4m4.5-4v4" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Seller Leads</h1>
              <p className="text-orange-100 text-sm mt-1">Manage and track your seller opportunities</p>
            </div>
          </div>
        </div>
        <div className="text-right text-orange-100 text-sm">
          <div className="font-semibold">{dummyLeads?.length || 0} Total Leads</div>
          <div>{filteredLeads.length} Matching Search</div>
        </div>
      </div>
    </div>
  );

  return (
    <PageContent title="Seller Leads" subtitle="All Leads">
      {error && (
        <div className="mx-8 mt-8 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      <div className="px-8 py-8 space-y-6">
        <HeroBanner />

        {/* SEARCH AND ACTION BAR */}
        <div className="flex flex-col gap-4">
          {/* Search Input with Icon */}
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[250px]">
              <svg
                className="absolute left-3 top-3 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search leads by name, address, or contact info..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 transition-colors"
              />
            </div>

            {selectedLeads.length > 0 && !showMap && (
              <button
                onClick={() => openModal("bulk_delete", null)}
                className="px-5 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors flex-shrink-0 whitespace-nowrap"
              >
                Delete ({selectedLeads.length})
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowMap(!showMap)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600 text-white transition-colors flex-shrink-0 flex items-center gap-2"
            >
              {showMap ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 11-4 0V5H8v8a2 2 0 11-4 0V5H2z" />
                  </svg>
                  List Mode
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Map Mode
                </>
              )}
            </button>
            <button
              onClick={() => setQuickLeadModalOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white transition-colors flex-shrink-0 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Quick Lead
            </button>
            <button
              onClick={() =>
                onViewChange(views.sellerEditScreen, "sellerleads", {})
              }
              className="px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 text-white transition-colors flex-shrink-0 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create Lead
            </button>
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">

        {/* Conditional Rendering: Map vs Table */}
        {showMap ? (
          <SellerPagesMapWrapper
            filteredLeads={filteredLeads}
            handleRowClick={handleRowClick}
            dummyLeads={dummyLeads}
            setDummyLeads={setDummyLeads}
          />
        ) : (
          <div className="overflow-y-visible">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-4 w-4">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-orange-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-orange-500 dark:focus:ring-orange-400 cursor-pointer"
                      onChange={handleSelectAll}
                      checked={
                        currentLeads.length > 0 &&
                        currentLeads.every((l) => selectedLeads.includes(l.pid))
                      }
                    />
                  </th>
                  <th className="px-6 py-4">Lead</th>
                  <th className="px-6 py-4">Details</th>
                  <th className="px-6 py-4 hidden 2xl:table-cell">Estimates</th>
                  <th className="px-6 py-4 hidden 2xl:table-cell">Recorded</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentLeads.length > 0 ? (
                  currentLeads.map((lead, index) => (
                    <tr
                      key={lead.pid}
                      className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.pid)}
                          onChange={() => handleSelectRow(lead.pid)}
                          className="w-4 h-4 text-orange-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-orange-500 dark:focus:ring-orange-400 cursor-pointer"
                        />
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer text-gray-900 dark:text-gray-200"
                        onClick={() => handleRowClick(lead)}
                      >
                        <div className="font-medium">{lead.property_owner_name}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{formatMobileNumber(lead.mobile_number)}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{lead.email_address}</div>
                        {lead.property_sequence !== "0" && (
                          <div className="mt-2">
                            <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              AR: {getResponderShortName(lead.property_sequence) || "Auto Generated"}
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer text-gray-900 dark:text-gray-200"
                        onClick={() => handleRowClick(lead)}
                      >
                        <div className="font-medium text-sm">{lead.property_address}, {lead.property_city}</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{lead.property_beds} bd • {lead.property_baths} ba</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">{parseInt(lead.property_sqft || 0).toLocaleString()} sqft</div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">Built: {lead.property_year_built}</div>
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer text-xs text-gray-900 dark:text-gray-200 hidden 2xl:table-cell"
                        onClick={() => handleRowClick(lead)}
                      >
                        <div className="text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-gray-900 dark:text-gray-200">Repairs:</span> ${parseInt(lead.property_repair_costs || 0).toLocaleString()}
                        </div>
                        <div className="text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-gray-900 dark:text-gray-200">Debt:</span> ${parseInt(lead.total_debt || 0).toLocaleString()}
                        </div>
                        <div className="text-gray-600 dark:text-gray-400">
                          <span className="font-medium text-gray-900 dark:text-gray-200">Zestimate:</span> ${parseInt(lead.zestimate2 || 0).toLocaleString()}
                        </div>
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer text-xs text-gray-600 dark:text-gray-400 hidden 2xl:table-cell"
                        onClick={() => handleRowClick(lead)}
                      >
                        {moment(lead.lead_date, "MM/DD/YYYY").format("LL")}
                      </td>
                      <td
                        className="px-6 py-4 cursor-pointer"
                        onClick={() => {
                          setSelectedTags(lead.status);
                          setPID(lead.pid);
                          setModalOpen(true);
                        }}
                      >
                        <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-colors">
                          {lead.status}
                        </span>
                      </td>
                      <td
                        className="px-6 py-4 text-center relative"
                        ref={(el) => (menuRef.current[index] = el)}
                      >
                        <button
                          onClick={() =>
                            setMenuOpenForUser(
                              menuOpenForUser === lead.pid ? null : lead.pid,
                            )
                          }
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          <svg
                            className="w-5 h-5 text-gray-500 dark:text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        {menuOpenForUser === lead.pid && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900">
                            <button
                              onClick={() => openModal("notes", lead)}
                              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                              My Notes
                            </button>
                            <button
                              onClick={() => openModal("podio", lead)}
                              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10.5 1.5H5a2 2 0 00-2 2v9a2 2 0 002 2h.006c.03.02.062.039.1.057l5.85-5.85a1.5 1.5 0 10-2.12-2.12L3.636 11.364a2 2 0 010-2.83l9.172-9.172a2 2 0 112.828 2.828l-7.07 7.07A3 3 0 1014 8a1 1 0 102 0 5 5 0 10-5.5 4.95z" />
                              </svg>
                              Send to Podio
                            </button>
                            <button
                              onClick={() => openModal("analysis", lead)}
                              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                              </svg>
                              Lead Analysis
                            </button>
                            <button
                              onClick={() => openModal("skip_trace", lead)}
                              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                              </svg>
                              Skiptrace
                            </button>
                            <button
                              onClick={() => openModal("auto_query", lead)}
                              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                              </svg>
                              AI Query
                            </button>
                            <div className="border-t border-gray-200 dark:border-gray-700">
                              <button
                                onClick={() =>
                                  setAutoresponderSubMenuOpen(
                                    autoresponderSubMenuOpen === lead.pid
                                      ? null
                                      : lead.pid,
                                  )
                                }
                                className="flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                                  </svg>
                                  Set Autoresponder
                                </div>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              {autoresponderSubMenuOpen === lead.pid && (
                                <div className="absolute right-full top-0 mr-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-30">
                                  <button
                                    onClick={() =>
                                      handleSetAutoresponder(lead.pid, 0)
                                    }
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                  >
                                    Remove Autoresponder
                                  </button>
                                  {sellerAR?.map((ar) => (
                                    <button
                                      key={ar.sequence}
                                      onClick={() =>
                                        handleSetAutoresponder(
                                          lead.pid,
                                          ar.sequence,
                                        )
                                      }
                                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between"
                                    >
                                      {ar.short_name}
                                      {lead.property_sequence === ar.sequence && (
                                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="border-t border-gray-200 dark:border-gray-700">
                              <button
                                onClick={() => openModal("delete", lead)}
                                className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-6 py-12 text-center"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                        </svg>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">No seller leads found</p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Try adjusting your search or create a new lead</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {filteredLeads.length > itemsPerPage && !showMap && (
          <div className="flex items-center justify-between gap-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 p-6 rounded-b-xl">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Previous
            </button>
            <div className="flex gap-1 justify-center flex-wrap">
              {renderPageNumbers()}
            </div>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        )}
        </div>
      </div>

      <Modal show={modalOpen} onClose={closeModal} title="Change Lead Status">
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Select a status for this lead:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {tagGroups.flat().map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  setSelectedTags(tag);
                  handleTagClick(tag);
                }}
                className={`
                  px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 border
                  ${
                    selectedTags === tag
                      ? "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700 shadow-md"
                      : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-orange-300 dark:hover:border-orange-600 hover:bg-orange-50 dark:hover:bg-gray-600"
                  }
                `}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          modalContent.action
            ? modalContent.action.replace(/_/g, " ").charAt(0).toUpperCase() +
              modalContent.action.replace(/_/g, " ").slice(1).toLowerCase()
            : "Confirm Action"
        }
        footer={renderModalFooter()}
      >
        <div className="space-y-4">
          {renderModalContent()}
        </div>
      </Modal>

      {/* Quick Lead Modal */}
      <Modal
        show={quickLeadModalOpen}
        onClose={() => setQuickLeadModalOpen(false)}
        title="Create Quick Lead"
        footer={renderQuickLeadFooter()}
        maxWidth="max-w-lg"
      >
          <form
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}

          >
            <div className="flex gap-2 ">
              <AutocompleteInput
                onPlaceSelected={handlePlaceSelected}
                placeholder="Enter property address"
                name="property_address"
                value={newLead.property_address}
                onChange={handleQuickLeadChange}
                autoComplete="off"
              />
            </div>
    <div className="flex items-start justify-between p-2 overflow-visible">
              <label
        className="text-xs font-medium text-gray-500 dark:text-gray-200 uppercase mr-4 pt-5"
        htmlFor="location-search-input"
      >
                City:
              </label>
                    <div className="w-full overflow-visible">

              <FormGroup
                autoComplete="off"
                name="property_city"
                value={newLead.property_city}
                onChange={handleQuickLeadChange}
                type="text"
                placeholder="City"
              />
            </div></div>
    <div className="flex items-start justify-between p-2 overflow-visible">
              <label
        className="text-xs font-medium text-gray-500 dark:text-gray-200 uppercase mr-4 pt-5"
        htmlFor="location-search-input"
      >
                State:
              </label>
              <div className="w-full overflow-y-visible">
              <SelectGroup
                autoComplete="off"
                name="property_state"
                value={newLead.property_state}
                onChange={handleQuickLeadChange}
                options={stateOptions}
              />
              </div>
            </div>
    <div className="flex items-start justify-between p-2 overflow-visible">
             <label
        className="text-xs font-medium text-gray-500 dark:text-gray-200 uppercase mr-4 pt-5"
        htmlFor="location-search-input"
      >
                Zip:
              </label>
<div className="w-full overflow-visible">
              <FormGroup
                autoComplete="off"
                name="property_zip"
                value={newLead.property_zip}
                onChange={handleQuickLeadChange}
                type="number"
                placeholder="Zip Code"
              />
            </div>
            </div>
          </form>
      </Modal>

      {/* NEW: AI Assistant Chat */}
      <AIAssistantChat leads={dummyLeads} user={user} />
    </PageContent>
  );
};

export default SellerPages;